import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "@env/environment";
import { lastValueFrom } from "rxjs";
import { OrderServiceApiResponse } from "@features/order-detail/models/order-services.model";
import { ApiRoutes } from "app/core/constants/api-routes";

type QueryType = "order-id" | "document" | "os-id";

@Injectable({ providedIn: "root" })
export class ServiceOrderDataService {
  private readonly http = inject(HttpClient);
  private readonly endpoint = `${environment.API_SERVICES}`;

  async fetchByOrderId(
    tenantId: string,
    orderId: string = "",
  ): Promise<OrderServiceApiResponse[]> {
    return this.fetchServices(tenantId, orderId, "order-id");
  }

  async fetchByDocument(
    tenantId: string,
    document: string = "",
  ): Promise<OrderServiceApiResponse[]> {
    return this.fetchServices(tenantId, document, "document");
  }

  async fetchByOsId(
    tenantId: string,
    osId: string = "",
  ): Promise<OrderServiceApiResponse[]> {
    return this.fetchServices(tenantId, osId, "os-id");
  }

  async fetchAll(tenantId: string): Promise<OrderServiceApiResponse[]> {
    const path = ApiRoutes.service.list.all(tenantId);
    const url = `${this.endpoint}${path}`;
    return lastValueFrom(this.http.get<OrderServiceApiResponse[]>(url));
  }

  async fetchWithMultipleParams(
    tenantId: string,
    documentValue?: string,
    documentType?: QueryType,
    osId?: string,
  ): Promise<OrderServiceApiResponse[]> {
    const baseUrl = `${this.endpoint}/api/customer-care/v2/${tenantId}/services`;
    const params = new URLSearchParams();

    if (documentValue && documentType) {
      const paramKey = documentType === "order-id" ? "order-id" : "document";
      params.append(paramKey, documentValue);
    }

    if (osId) {
      params.append("os-id", osId);
    }

    const queryString = params.toString();
    const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

    return lastValueFrom(this.http.get<OrderServiceApiResponse[]>(url));
  }

  private async fetchServices(
    tenantId: string,
    value: string,
    type: QueryType,
  ): Promise<OrderServiceApiResponse[]> {
    if (!value) {
      return this.fetchAll(tenantId);
    }

    let path = "";
    switch (type) {
      case "order-id":
        path = ApiRoutes.service.list.byOrderId(tenantId, value);
        break;
      case "document":
        path = ApiRoutes.service.list.byDocument(tenantId, value);
        break;
      case "os-id":
        path = ApiRoutes.service.list.byOsId(tenantId, value);
        break;
    }

    const url = `${this.endpoint}${path}`;
    return lastValueFrom(this.http.get<OrderServiceApiResponse[]>(url));
  }
}
